import { z } from 'zod'

import { ModuleTypeEnum } from './module'

/**
 * Enum for permission actions that can be performed on modules
 */
export enum PermissionActionEnum {
  READ = 'READ', // View data
  CREATE = 'CREATE', // Create new records
  UPDATE = 'UPDATE', // Edit existing records
  DELETE = 'DELETE', // Delete records
  MANAGE = 'MANAGE', // Administrative access (change settings)
  APPROVE = 'APPROVE', // Approve/reject items
}

/**
 * Error codes for permission operations
 */
export const PermissionErrorCode = {
  NOT_FOUND: 'PERMISSION_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED_ACCESS',
  INVALID_ASSIGNMENT: 'INVALID_PERMISSION_ASSIGNMENT',
  SERVER_ERROR: 'PERMISSION_SERVER_ERROR',
} as const

export type PermissionErrorCode =
  (typeof PermissionErrorCode)[keyof typeof PermissionErrorCode]

/**
 * Permission definition schema
 */
export const PermissionDefinitionSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
  isLimited: z.boolean().default(false),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionDefinition = z.infer<typeof PermissionDefinitionSchema>

/**
 * Permission set schema
 */
export const PermissionSetSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  systemKey: z.string().nullable().optional(),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionSet = z.infer<typeof PermissionSetSchema>

/**
 * Permission set item schema
 */
export const PermissionSetItemSchema = z.object({
  id: z.string().uuid().optional(),
  permissionSetId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  createdAt: z.date().optional(),
})

export type PermissionSetItem = z.infer<typeof PermissionSetItemSchema>

/**
 * Permission assignment schema (individual permissions to members)
 */
export const PermissionAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  memberId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  resourceId: z.string().uuid().nullable().optional(),
  resourceType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionAssignment = z.infer<typeof PermissionAssignmentSchema>

/**
 * Permission set assignment schema
 */
export const PermissionSetAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  memberId: z.string().uuid(),
  permissionSetId: z.string().uuid(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionSetAssignment = z.infer<
  typeof PermissionSetAssignmentSchema
>

/**
 * DTOs for permission operations
 */

export const CreatePermissionDefinitionDtoSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)),
  isSystem: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
  isLimited: z.boolean().optional().default(false),
})

export type CreatePermissionDefinitionDto = z.infer<
  typeof CreatePermissionDefinitionDtoSchema
>

export const UpdatePermissionDefinitionDtoSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum).optional(),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)).optional(),
  isActive: z.boolean().optional(),
})

export type UpdatePermissionDefinitionDto = z.infer<
  typeof UpdatePermissionDefinitionDtoSchema
>

export const CreatePermissionSetDtoSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  permissionIds: z.array(z.string().uuid()),
  isSystem: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
})

export type CreatePermissionSetDto = z.infer<
  typeof CreatePermissionSetDtoSchema
>

export const UpdatePermissionSetDtoSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  permissionIds: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional(),
})

export type UpdatePermissionSetDto = z.infer<
  typeof UpdatePermissionSetDtoSchema
>

export const AssignPermissionDtoSchema = z.object({
  memberId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  resourceId: z.string().uuid().optional(),
  resourceType: z.nativeEnum(ModuleTypeEnum).optional(),
})

export type AssignPermissionDto = z.infer<typeof AssignPermissionDtoSchema>

export const AssignPermissionSetDtoSchema = z.object({
  memberId: z.string().uuid(),
  permissionSetId: z.string().uuid(),
})

export type AssignPermissionSetDto = z.infer<
  typeof AssignPermissionSetDtoSchema
>

/**
 * Helper function to check if a permission includes a specific action
 */
export function hasPermissionAction(
  permissions: PermissionDefinition[],
  moduleType: ModuleTypeEnum,
  action: PermissionActionEnum,
): boolean {
  return permissions.some(
    p =>
      p.moduleType === moduleType &&
      p.allowedActions.includes(action) &&
      p.isActive,
  )
}

/**
 * Utility to create a full permission set for a module (all actions)
 */
export function createFullModulePermission(
  moduleType: ModuleTypeEnum,
  namePrefix: string = '',
): PermissionDefinition {
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.LEAD:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.BILLING_AUTHORIZATION:
      moduleName = 'Billing Authorizations'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.CUSTOM_FIELD:
      moduleName = 'Custom Fields'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.MEMBER:
      moduleName = 'Members'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.TODAY:
      moduleName = 'Today'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.AI:
      moduleName = 'AI'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    case ModuleTypeEnum.INTEGRATION:
      moduleName = 'Integrations'
      break
    default:
      moduleName = String(moduleType)
  }

  return {
    id: `${moduleType.toLowerCase()}_full_access`,
    name: `${namePrefix}${namePrefix ? ' ' : ''}${moduleName} - Full Access`,
    description: `Full access to all ${moduleName.toLowerCase()} features`,
    moduleType: moduleType,
    allowedActions: Object.values(PermissionActionEnum),
    isSystem: true,
    isActive: true,
    isLimited: false,
  }
}

/**
 * Utility to create permissions for a Standard Access level
 * Includes access to core collaboration modules
 */
export function createRegularUserPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.CUSTOM_FIELD:
      moduleName = 'Custom Fields'
      break
    case ModuleTypeEnum.MEMBER:
      moduleName = 'Members'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.INTEGRATION:
      moduleName = 'Integrations'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.LEAD:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.TODAY:
      moduleName = 'Today'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.AI:
      moduleName = 'AI'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    default:
      return null as any // Don't create permissions for other modules
  }

  // Regular users get full CRUD access but with some limitations
  let allowedActions = [
    PermissionActionEnum.READ,
    PermissionActionEnum.CREATE,
    PermissionActionEnum.UPDATE,
    PermissionActionEnum.DELETE,
  ]

  if (moduleType === ModuleTypeEnum.MEMBER) {
    allowedActions = [PermissionActionEnum.READ]
  }

  if (moduleType === ModuleTypeEnum.CALENDAR) {
    allowedActions = [
      PermissionActionEnum.READ,
      PermissionActionEnum.CREATE,
      PermissionActionEnum.UPDATE,
    ]
  }

  // Add APPROVE action for tasks (can approve their own tasks)
  if (moduleType === ModuleTypeEnum.TASK) {
    allowedActions.push(PermissionActionEnum.APPROVE)
  }

  let description = `Standard access to ${moduleName.toLowerCase()} with ability to create, view, update, and delete records`

  if (moduleType === ModuleTypeEnum.MEMBER) {
    description =
      'Standard read access to members for viewing and collaboration purposes'
  }

  if (moduleType === ModuleTypeEnum.CALENDAR) {
    description =
      'Standard access to calendar with ability to view, create, and update entries'
  }

  if (moduleType === ModuleTypeEnum.AI) {
    allowedActions = [PermissionActionEnum.READ]
    description = 'Standard access to AI assistant features'
  }

  if (moduleType === ModuleTypeEnum.WORKSTREAM) {
    allowedActions = [PermissionActionEnum.READ]
    description = 'Standard access to workstreams you belong to'
  }

  return {
    id: `standard_access_${moduleType.toLowerCase()}`,
    name: `${moduleName} - Standard Access`,
    description,
    moduleType: moduleType,
    allowedActions,
    isSystem: true,
    isActive: true,
    isLimited: true, // Limited to their own records in most cases
  }
}

/**
 * Utility to create permissions for a Full Admin access level
 * Full administrative access to all modules
 */
export function createFullAdminPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.LEAD:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.BILLING_AUTHORIZATION:
      moduleName = 'Billing Authorizations'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.MEMBER:
      moduleName = 'Members'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.TODAY:
      moduleName = 'Today'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    default:
      moduleName = String(moduleType)
  }

  return {
    id: `full_admin_${moduleType.toLowerCase()}`,
    name: `Full Admin - ${moduleName}`,
    description: `Full administrative access to all ${moduleName.toLowerCase()} features including management capabilities`,
    moduleType: moduleType,
    allowedActions: Object.values(PermissionActionEnum),
    isSystem: true,
    isActive: true,
    isLimited: false, // No limitations for full admin
  }
}
